
import { useEffect, useState } from 'react';

export function DayCurveChart() {
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedView, setSelectedView] = useState<'power' | 'energy' | 'comparison'>('power');

  useEffect(() => {
    const generateDayData = () => {
      const data = [];
      const now = new Date();
      const currentHour = now.getHours();

      for (let hour = 0; hour < 24; hour++) {
        const isDaytime = hour >= 6 && hour <= 18;
        const isCurrentOrPast = hour <= currentHour;

        // Curva de producción solar realista
        let pvPower = 0;
        let pvEnergy = 0;
        if (isDaytime && isCurrentOrPast) {
          const solarCurve = Math.sin(((hour - 6) / 12) * Math.PI);
          pvPower = Math.floor(solarCurve * (3000 + Math.random() * 500));
          pvEnergy = pvPower / 1000; // kW
        }

        // Curva de consumo realista
        let loadPower = 800; // Consumo base
        if (hour >= 6 && hour <= 9) loadPower += 600; // Mañana
        if (hour >= 12 && hour <= 14) loadPower += 300; // Mediodía
        if (hour >= 18 && hour <= 22) loadPower += 800; // Noche
        loadPower += Math.random() * 300;

        if (!isCurrentOrPast) {
          loadPower = 800 + Math.random() * 200; // Predicción
        }

        const loadEnergy = loadPower / 1000;
        const gridPower = loadPower - pvPower;
        const gridEnergy = gridPower / 1000;
        const selfConsumption = Math.min(pvPower, loadPower);
        const selfConsumptionEnergy = selfConsumption / 1000;

        data.push({
          hour: `${hour.toString().padStart(2, '0')}:00`,
          hourNum: hour,
          pvPower: Math.floor(pvPower),
          loadPower: Math.floor(loadPower),
          gridPower: Math.floor(gridPower),
          selfConsumption: Math.floor(selfConsumption),
          pvEnergy: Number(pvEnergy.toFixed(2)),
          loadEnergy: Number(loadEnergy.toFixed(2)),
          gridEnergy: Number(gridPower.toFixed(2)),
          selfConsumptionEnergy: Number(selfConsumptionEnergy.toFixed(2)),
          isCurrentOrPast,
          isCurrent: hour === currentHour
        });
      }

      setChartData(data);
    };

    generateDayData();
  }, []);

  const maxValue = Math.max(...chartData.map(d => {
    if (selectedView === 'power') return Math.max(d.pvPower, d.loadPower);
    if (selectedView === 'energy') return Math.max(d.pvEnergy, d.loadEnergy);
    return Math.max(d.pvPower, d.loadPower);
  }));

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
      {/* Controles */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setSelectedView('power')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedView === 'power'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
          >
            Potencia (W)
          </button>
          <button
            onClick={() => setSelectedView('energy')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedView === 'energy'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
          >
            Energía (kW)
          </button>
          <button
            onClick={() => setSelectedView('comparison')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedView === 'comparison'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
          >
            Comparación
          </button>
        </div>

        <div className="flex items-center space-x-4 text-xs">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
            <span>Producción Solar</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
            <span>Consumo Hogar</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-400 rounded-full"></div>
            <span>Autoconsumo</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
            <span>Predicción</span>
          </div>
        </div>
      </div>

      {/* Gráfico */}
      <div className="relative h-80 overflow-x-auto">
        <div className="flex items-end justify-between h-full min-w-full">
          {chartData.map((data, index) => {
            const pvValue = selectedView === 'energy' ? data.pvEnergy : data.pvPower;
            const loadValue = selectedView === 'energy' ? data.loadEnergy : data.loadPower;
            const selfValue = selectedView === 'energy' ? data.selfConsumptionEnergy : data.selfConsumption;

            const pvHeight = maxValue > 0 ? (pvValue / maxValue) * 100 : 0;
            const loadHeight = maxValue > 0 ? (loadValue / maxValue) * 100 : 0;
            const selfHeight = maxValue > 0 ? (selfValue / maxValue) * 100 : 0;

            return (
              <div key={index} className="flex flex-col items-center space-y-2 group cursor-pointer flex-1 max-w-12">
                {/* Líneas de conexión para vista de comparación */}
                {selectedView === 'comparison' && (
                  <div className="relative h-64 w-full">
                    {/* Área de autoconsumo */}
                    <div
                      className="absolute bottom-0 w-full bg-green-200 opacity-60 rounded-t-sm"
                      style={{ height: `${selfHeight}%` }}
                    ></div>

                    {/* Línea de producción */}
                    <div
                      className={`absolute bottom-0 w-1 bg-yellow-500 rounded-full ${!data.isCurrentOrPast ? 'opacity-50 border-dashed border-2 border-yellow-400' : ''}`}
                      style={{ height: `${pvHeight}%`, left: '25%' }}
                    ></div>

                    {/* Línea de consumo */}
                    <div
                      className={`absolute bottom-0 w-1 bg-blue-500 rounded-full ${!data.isCurrentOrPast ? 'opacity-50 border-dashed border-2 border-blue-400' : ''}`}
                      style={{ height: `${loadHeight}%`, right: '25%' }}
                    ></div>

                    {/* Indicador de hora actual */}
                    {data.isCurrent && (
                      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-red-500 animate-pulse"></div>
                    )}
                  </div>
                )}

                {/* Vista de barras para power y energy */}
                {selectedView !== 'comparison' && (
                  <div className="relative flex items-end space-x-1 h-64">
                    {/* Producción Solar */}
                    <div
                      className={`w-3 bg-gradient-to-t from-yellow-400 to-yellow-300 rounded-t-sm transition-all duration-300 group-hover:from-yellow-500 group-hover:to-yellow-400 ${!data.isCurrentOrPast ? 'opacity-50 border-dashed border-2 border-yellow-400' : ''}`}
                      style={{ height: `${pvHeight}%` }}
                      title={`Producción: ${pvValue}${selectedView === 'energy' ? 'kW' : 'W'}`}
                    ></div>

                    {/* Consumo */}
                    <div
                      className={`w-3 bg-gradient-to-t from-blue-500 to-blue-400 rounded-t-sm transition-all duration-300 group-hover:from-blue-600 group-hover:to-blue-500 ${!data.isCurrentOrPast ? 'opacity-50 border-dashed border-2 border-blue-400' : ''}`}
                      style={{ height: `${loadHeight}%` }}
                      title={`Consumo: ${loadValue}${selectedView === 'energy' ? 'kW' : 'W'}`}
                    ></div>

                    {/* Indicador de hora actual */}
                    {data.isCurrent && (
                      <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                        <div className="w-2 h-2 bg-red-500 rounded-full animate-ping"></div>
                      </div>
                    )}
                  </div>
                )}

                {/* Hora */}
                <span className={`text-xs ${data.isCurrent ? 'text-red-600 font-bold' : 'text-gray-500'} transform -rotate-45 origin-center whitespace-nowrap`}>
                  {data.hour}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Resumen estadístico */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
        <div className="text-center">
          <div className="text-lg font-bold text-yellow-700">
            {chartData.filter(d => d.isCurrentOrPast).reduce((sum, d) => sum + (selectedView === 'energy' ? d.pvEnergy : d.pvPower), 0).toLocaleString()}
          </div>
          <div className="text-xs text-gray-600">
            Producción Total {selectedView === 'energy' ? '(kW)' : '(W)'}
          </div>
        </div>

        <div className="text-center">
          <div className="text-lg font-bold text-blue-700">
            {chartData.filter(d => d.isCurrentOrPast).reduce((sum, d) => sum + (selectedView === 'energy' ? d.loadEnergy : d.loadPower), 0).toLocaleString()}
          </div>
          <div className="text-xs text-gray-600">
            Consumo Total {selectedView === 'energy' ? '(kW)' : '(W)'}
          </div>
        </div>

        <div className="text-center">
          <div className="text-lg font-bold text-green-700">
            {chartData.filter(d => d.isCurrentOrPast).reduce((sum, d) => sum + (selectedView === 'energy' ? d.selfConsumptionEnergy : d.selfConsumption), 0).toLocaleString()}
          </div>
          <div className="text-xs text-gray-600">
            Autoconsumo {selectedView === 'energy' ? '(kW)' : '(W)'}
          </div>
        </div>

        <div className="text-center">
          <div className="text-lg font-bold text-purple-700">
            {Math.max(...chartData.filter(d => d.isCurrentOrPast).map(d => selectedView === 'energy' ? d.pvEnergy : d.pvPower)).toLocaleString()}
          </div>
          <div className="text-xs text-gray-600">
            Pico Máximo {selectedView === 'energy' ? '(kW)' : '(W)'}
          </div>
        </div>
      </div>
    </div>
  );
}
